import { ShieldCheck, UserCheck, Gauge, LayoutDashboard, GraduationCap, PhoneCall, Boxes, ThumbsUp, Rocket } from "lucide-react";

const INK = "#1C2B21";
const CYAN_DARK = "#3E6B4A";
const PINK_DARK = "#1C2B21";

const SUITE = [
  {
    Icon: UserCheck,
    t: "Fractional CCO",
    b: "A named Chief Compliance Officer who owns your program — exam-ready policies, board reporting, bank-partner and regulator conversations. Not a hotline; a person.",
  },
  {
    Icon: ShieldCheck,
    t: "BSA/AML Officer",
    b: "Designated BSA/AML officer for a FinCEN-covered mortgage lender — OFAC screening, alert triage, SAR investigation and drafting on the regulator's calendar.",
  },
  {
    Icon: Gauge,
    t: "Risk Assessment",
    b: "A living, mortgage-specific risk assessment — TRID, HMDA, fair lending, UDAAP — refreshed as your products, states, and volumes change, not a PDF that ages in a drawer.",
  },
  {
    Icon: LayoutDashboard,
    t: "Compliance Management System",
    b: "The CMS platform under it all: policies, complaints, marketing review, vendor & bank-partner oversight, monitoring & testing, issue tracking — one login, one audit trail.",
  },
  {
    Icon: GraduationCap,
    t: "Training Program",
    b: "A role-based program for loan officers, processing, and underwriting — 48-hour course turnaround, CFPB and state rule updates included, tracking and attestations built in.",
  },
];

const PATH = [
  { Icon: PhoneCall, n: "1", t: "Discovery call", b: "Free — we learn your origination flow, investors, and state footprint." },
  { Icon: Boxes, n: "2", t: "Prototype", b: "No cost, no commitment — configured to Sage Home Loans in ~2 weeks." },
  { Icon: ThumbsUp, n: "3", t: "You decide", b: "Go live, or walk away — no obligation either way." },
  { Icon: Rocket, n: "4", t: "Go live", b: "Live with CMS and fractional team within 45 days — ahead of your next exam cycle." },
];

export function PlatformSuite() {
  return (
    <section id="suite" className="anchor-target py-14 md:py-20">
      <span className="sg-eyebrow">The full suite · built for a digital lender</span>
      <h2 className="sg-h text-4xl md:text-5xl mt-3 max-w-3xl" data-testid="suite-headline">
        An outsourced compliance department — <span className="sg-grad-text">under one roof, at one price.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        Five functions most early-stage fintechs staff separately (or skip until an investor or
        a state examiner forces the issue). You get all of them from one team that already knows consumer
        lending — and you only pay for the fractional hours you actually use.
      </p>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SUITE.map(({ Icon, t, b }, i) => (
          <div key={t} className="sg-card p-6" data-testid={`suite-card-${i}`}>
            <div className="h-10 w-10 rounded-xl grid place-items-center mb-4"
                 style={{ background: "#B7D3A833" }}>
              <Icon className="h-5 w-5" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
            </div>
            <div className="sg-h text-lg mb-2" style={{ color: INK }}>{t}</div>
            <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
        <div className="sg-card p-6 flex flex-col justify-center" style={{ background: `${PINK_DARK}06`, borderColor: `${PINK_DARK}30` }}>
          <div className="sg-h text-lg mb-2" style={{ color: PINK_DARK }}>Why one roof?</div>
          <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.75 }}>
            Your CCO, your BSA officer, your training, and your CMS all read from the same data.
            No hand-offs, no "that's the other vendor's module," no gaps for an investor or
            an examiner to find.
          </p>
        </div>
      </div>
    </section>
  );
}

export function CommitmentPath() {
  return (
    <section id="path" className="anchor-target py-14 md:py-20">
      <span className="sg-eyebrow">How to work with us</span>
      <h2 className="sg-h text-4xl md:text-5xl mt-3 max-w-3xl">
        No cost to see if this is a fit — <span className="sg-grad-text">you only pay once you go live.</span>
      </h2>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PATH.map(({ Icon, n, t, b }) => (
          <div key={n} className="sg-card p-6 relative" data-testid={`path-step-${n}`}>
            <div className="absolute top-4 right-5 sg-h text-4xl" style={{ color: "#B7D3A855" }}>{n}</div>
            <Icon className="h-5 w-5 mb-3" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
            <div className="sg-h text-base mb-1.5" style={{ color: INK }}>{t}</div>
            <p className="text-[13px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 sg-card p-6 md:p-7" style={{ borderColor: `${CYAN_DARK}40`, background: "#B7D3A812" }} data-testid="commitment-45">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="sg-h text-5xl flex-shrink-0" style={{ color: CYAN_DARK }}>45<span className="text-xl align-top ml-1">days</span></div>
          <div>
            <div className="sg-h text-lg" style={{ color: INK }}>The CompliSun Commitment</div>
            <p className="text-[14px] leading-relaxed mt-1" style={{ color: INK, opacity: 0.78 }}>
              Once we agree on the prototype, we commit to a fully configured, tested, and <strong>live</strong> compliance
              program — built around Sage Home Loans's actual workflows — within 45 days of kickoff. If we miss that date
              for reasons within our control, <strong>your first 2 months of platform fees are on us.</strong> That's not
              a sales promise — it's a deadline we design the engagement around from day one.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
